'use client';

import { useState, useEffect } from 'react';
import AssistantFab from './AssistantFab';
import AssistantHub from './AssistantHub';
import { useEditor } from '@/context/EditorContext';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/app/api/client';

import { useLocation } from 'react-router-dom';
import { tenantSettingsApi } from '@/services/tenant-settings-api';

export default function Assistant() {
  const [isHubOpen, setIsHubOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  /* 
   * Pending task state
   * - hasPending: boolean
   * - pendingCount: number (for determining tooltip text)
   * - pendingLabel: string (specific task name if count === 1)
   */
  const [pendingState, setPendingState] = useState({
    hasPending: false,
    count: 0,
    label: ''
  });

  const [initialMode, setInitialMode] = useState<string | undefined>(undefined);

  const { isAgentMode } = useEditor();
  const { user } = useAuth(); // Need user to check firstTime

  // Use pathname to hide FAB on specific pages
  const isContentStrategyPage = pathname?.startsWith('/user/content-strategy');

  // Check for pending prompts
  const checkPendingPrompts = async () => {
    try {
      const { data } = await apiClient.get('/api/assistant/has-pending-task');
      setPendingState({
        hasPending: data.hasPending,
        count: data.count || 0,
        label: data.nextTask?.label || ''
      });
    } catch (error) {
      console.error('Failed to check pending prompts:', error);
    }
  };

  useEffect(() => {
    if (user) {
      checkPendingPrompts();
    }
  }, [user]);

  // Listen for custom event to open assistant (for auto-open)
  useEffect(() => {
    const handleOpenAssistant = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.mode) {
        setInitialMode(customEvent.detail.mode);
      }
      setIsHubOpen(true);
    };

    window.addEventListener('open-assistant', handleOpenAssistant);
    return () => window.removeEventListener('open-assistant', handleOpenAssistant);
  }, []);

  // Auto-open for first-time users
  useEffect(() => {
    if (user && user.firstTime && !isHubOpen) {
      setInitialMode('ONBOARDING');
      setIsHubOpen(true);
    }
  }, [user]);

  // Handle prompt answered/dismissed - refresh badge
  const handlePromptHandled = () => {
    setPendingState(prev => ({ ...prev, hasPending: false, count: 0 }));
  };

  const handleClose = () => {
    setIsHubOpen(false);
    setInitialMode(undefined); // Reset mode on close
  };

  if (isAgentMode) return null;

  return (
    <>
      {!isContentStrategyPage && (
        <AssistantFab
          onClick={() => setIsHubOpen(true)}
          badge={pendingState.hasPending}
          pendingCount={pendingState.count}
          pendingLabel={pendingState.label}
        />
      )}

      <AssistantHub
        isOpen={isHubOpen}
        onClose={handleClose}
        onPromptHandled={handlePromptHandled}
        initialMode={initialMode}
      />
    </>
  );
}
